import { useEffect, useState } from 'react'
import { api, ApiError, type AdminFeature } from '../api'
import { useAuth } from '../auth'
import { Topbar } from '../components/Topbar'
import { StatusLight } from '../components/StatusLight'
import { Button } from '../components/Button'
import { Input, Textarea } from '../components/Input'
import { EmptyState } from '../components/EmptyState'
import { useToast } from '../components/Toast'

export function AdminFeaturesPage() {
  const { user: me } = useAuth()
  const toast = useToast()
  const [features, setFeatures] = useState<AdminFeature[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [editId, setEditId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  async function refresh() {
    setLoading(true)
    setLoadError(null)
    try {
      const list = await api.listAdminFeatures()
      // Server returns all features (enabled + disabled). Sort by sortOrder then name.
      list.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      setFeatures(list)
    } catch (e) {
      setLoadError(
        e instanceof ApiError
          ? `Couldn't load features (HTTP ${e.status}). Check the server.`
          : 'Network error — retry or refresh.',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  if (me && !me.isOwner) {
    return (
      <div className="page">
        <Topbar />
        <main className="page-main">
          <EmptyState
            mark="restricted"
            title="Owner only"
            body="You need an owner account to manage features."
          />
        </main>
      </div>
    )
  }

  async function onToggle(f: AdminFeature) {
    setBusyId(f.id)
    try {
      await api.patchFeature(f.pluginId, { isEnabled: !f.isEnabled })
      setFeatures((prev) =>
        prev.map((x) => (x.id === f.id ? { ...x, isEnabled: !f.isEnabled } : x)),
      )
      toast.show(f.isEnabled ? 'Disabled' : 'Enabled', 'info')
    } catch (e) {
      toast.show(`Couldn't update (HTTP ${e instanceof ApiError ? e.status : '?'}).`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  async function onMove(f: AdminFeature, direction: -1 | 1) {
    const idx = features.findIndex((x) => x.id === f.id)
    const swapIdx = idx + direction
    if (idx < 0 || swapIdx < 0 || swapIdx >= features.length) return
    const other = features[swapIdx]
    setBusyId(f.id)
    try {
      // Swap sortOrder between two features. Use a halfway value to avoid collisions.
      const a = f.sortOrder
      const b = other.sortOrder
      // If equal, derive a new order from index
      const newA = a === b ? idx * 2 : (a + b) / 2
      const newB = a === b ? swapIdx * 2 : b
      await Promise.all([
        api.patchFeature(f.pluginId, { sortOrder: newA }),
        api.patchFeature(other.pluginId, { sortOrder: newB }),
      ])
      setFeatures((prev) => {
        const next = [...prev]
        next[idx] = { ...f, sortOrder: newA }
        next[swapIdx] = { ...other, sortOrder: newB }
        next.sort((x, y) => x.sortOrder - y.sortOrder || x.name.localeCompare(y.name))
        return next
      })
    } catch (e) {
      toast.show(`Couldn't reorder (HTTP ${e instanceof ApiError ? e.status : '?'}).`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="page">
      <Topbar />
      <main className="page-main">
        <div className="page-heading">
          <div>
            <h1>Features</h1>
            <div className="page-heading-sub">
              Enable plugins, change order, and edit the public name and description.
            </div>
          </div>
          <Button variant="secondary" onClick={refresh} disabled={loading}>
            Refresh
          </Button>
        </div>

        {loading && <div className="muted">Loading…</div>}
        {loadError && (
          <div className="confirm-block danger">
            <div className="confirm-block-title">{loadError}</div>
            <div>
              <Button variant="secondary" onClick={refresh}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {!loading && !loadError && features.length === 0 && (
          <EmptyState
            mark="no plugins"
            title="No features registered"
            body="Drop a plugin directory under server/src/plugins/ and restart the server. It'll appear here."
          />
        )}

        {!loading && !loadError && features.length > 0 && (
          <div className="admin-list">
            <div className="admin-list-header">
              <span>plugins</span>
              <span>{features.length}</span>
            </div>
            <div className="admin-list-items">
              {features.map((f, i) => (
                <FeatureRow
                  key={f.id}
                  feature={f}
                  isFirst={i === 0}
                  isLast={i === features.length - 1}
                  editOpen={editId === f.id}
                  busy={busyId === f.id}
                  onToggle={() => onToggle(f)}
                  onMoveUp={() => onMove(f, -1)}
                  onMoveDown={() => onMove(f, 1)}
                  onEdit={() => setEditId(editId === f.id ? null : f.id)}
                  onSaved={(updated) => {
                    setFeatures((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                    setEditId(null)
                    toast.show('Saved', 'info')
                  }}
                  onError={(m) => toast.show(m, 'error')}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

function FeatureRow({
  feature,
  isFirst,
  isLast,
  editOpen,
  busy,
  onToggle,
  onMoveUp,
  onMoveDown,
  onEdit,
  onSaved,
  onError,
}: {
  feature: AdminFeature
  isFirst: boolean
  isLast: boolean
  editOpen: boolean
  busy: boolean
  onToggle: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onEdit: () => void
  onSaved: (f: AdminFeature) => void
  onError: (m: string) => void
}) {
  return (
    <div className={feature.isEnabled ? 'feature-row' : 'feature-row disabled'}>
      <div className="feature-row-icon" aria-hidden="true">
        {feature.icon || '·'}
      </div>
      <div className="feature-row-text">
        <div className="feature-row-name">
          <span className="row-light-gap" aria-hidden="true">
            <StatusLight state={feature.isEnabled ? 'on' : 'off'} />
          </span>
          {feature.name}
        </div>
        <div className="feature-row-id">{feature.pluginId}</div>
        {feature.description && (
          <div className="muted feature-desc">
            {feature.description}
          </div>
        )}
        {editOpen && (
          <EditForm
            feature={feature}
            onCancel={onEdit}
            onSaved={onSaved}
            onError={onError}
          />
        )}
      </div>
      <div className="feature-row-actions">
        <label className="admin-toggle" title={feature.isEnabled ? 'Disable' : 'Enable'}>
          <input type="checkbox" checked={feature.isEnabled} onChange={onToggle} disabled={busy} />
        </label>
        <div className="feature-row-reorder">
          <button type="button" onClick={onMoveUp} disabled={isFirst || busy} aria-label="Move up">
            ▲
          </button>
          <button type="button" onClick={onMoveDown} disabled={isLast || busy} aria-label="Move down">
            ▼
          </button>
        </div>
        <Button variant="ghost" size="sm" onClick={onEdit} disabled={busy}>
          {editOpen ? 'Close' : 'Edit'}
        </Button>
      </div>
    </div>
  )
}

function EditForm({
  feature,
  onCancel,
  onSaved,
  onError,
}: {
  feature: AdminFeature
  onCancel: () => void
  onSaved: (f: AdminFeature) => void
  onError: (m: string) => void
}) {
  const [name, setName] = useState(feature.name)
  const [description, setDescription] = useState(feature.description)
  const [icon, setIcon] = useState(feature.icon)
  const [busy, setBusy] = useState(false)

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    if (name.trim().length < 1) {
      onError('Name is required.')
      return
    }
    setBusy(true)
    try {
      await api.patchFeature(feature.pluginId, {
        name: name.trim(),
        description: description.trim(),
        icon: icon.trim(),
      })
      onSaved({
        ...feature,
        name: name.trim(),
        description: description.trim(),
        icon: icon.trim(),
      })
    } catch (err) {
      onError(`Couldn't save (HTTP ${err instanceof ApiError ? err.status : '?'}).`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={onSave}
      className="surface-flush feature-edit-form"
    >
      <div className="admin-form-row">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          required
        />
        <Input
          label="Icon"
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          hint="Single emoji or character."
          disabled={busy}
          maxLength={8}
        />
      </div>
      <div className="feature-edit-row">
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          hint="Shown on the home card."
          disabled={busy}
        />
      </div>
      <div className="admin-actions-end feature-edit-actions">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}
